import React, { useMemo } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { ContactDetails } from '../../../primary-record-types/DynamoTypes';
import { FastField, Field, getIn, useFormikContext } from 'formik';
import {
    Validator,
    composeValidators,
    email,
    faxNumber,
    getDateFormatValidator,
    phoneNumber,
    required as requiredValidator,
} from '../../../utils/validators';
import { getIntl } from '../../../utils/intl';
import { handleFormikSubmit } from '../../../utils/handleFormikSubmit';
import { isPresent } from 'ts-is-present';
import { SelectOption } from '../../v2BaseComponents/Inputs/Select/types';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import { Form, SplitFormRow } from '../../v2BaseComponents/Form';
import { Action } from '../../v2BaseComponents/ActionButtonLayout/types';
import { FormStack } from '../../v2BaseComponents/Stack';
import _chunk from 'lodash/chunk';
import { IconName } from '../../v2BaseComponents/Icon';

const intl = getIntl('contactDetailsForm', {
    firstName: 'First Name',
    lastName: 'Last Name',
    company: 'Company',
    dob: 'Date of Birth',
    sex: 'Sex',
    gender: 'Gender',
    email: 'Email',
    preferredFirstName: 'Preferred Name',
    phoneNumber: 'Phone Number',
    address1: 'Street Address 1',
    address2: 'Street Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    save: 'Save',
    relationship: 'Relationship',
    male: 'Male',
    female: 'Female',
    nonBinary: 'Non-Binary',
    declinedAnswer: 'Declined to answer',
    faxNumber: 'Fax Number',
    url: 'URL',
    role: 'Role',
    description: 'Description',
    nameOrCompanyRequired: 'Name or Company is required',
    selectGender: 'Select Gender',
    selectSex: 'Select Sex',
});

export type ContactDetailField =
    | 'firstName'
    | 'lastName'
    | 'company'
    | 'preferredFirstName'
    | 'dob'
    | 'sex'
    | 'gender'
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'zipCode'
    | 'email'
    | 'phoneNumber'
    | 'relationship'
    | 'description'
    | 'faxNumber'
    | 'url'
    | 'role';

const allContactDetailFields: ContactDetailField[] = [
    'firstName',
    'lastName',
    'company',
    'preferredFirstName',
    'dob',
    'sex',
    'gender',
    'address1',
    'address2',
    'city',
    'state',
    'zipCode',
    'email',
    'phoneNumber',
    'relationship',
    'description',
    'faxNumber',
    'url',
    'role',
];

const sexOptions: SelectOption[] = [
    {
        label: intl('male'),
        value: 'male',
    },
    {
        label: intl('female'),
        value: 'female',
    },
];

const genderOptions: SelectOption[] = [
    {
        label: intl('male'),
        value: 'male',
    },
    {
        label: intl('female'),
        value: 'female',
    },
    {
        label: intl('nonBinary'),
        value: 'non-binary',
    },
    {
        label: intl('declinedAnswer'),
        value: 'declined-answer',
    },
];

export type CustomValidator = (
    value: any,
    allValues: Partial<ContactDetails>,
    allTouched: Partial<Record<ContactDetailField, boolean>>,
) => string | undefined;

export type CustomValidators = Partial<Record<ContactDetailField, CustomValidator>>;

export type ContactDetailsFormOwnProps = {
    title?: string;
    fieldNamePrefix?: string;
    initialValues?: Partial<ContactDetails>;
    onSubmit: (values: ContactDetails) => void;
    fields: ContactDetailField[];
    required?: ContactDetailField[];
    submitButtonTitle: string;
    style?: StyleProp<ViewStyle>;
    isDisabled?: boolean;
    isSubmitting?: boolean;
    renderFormStart?: () => React.ReactElement;
    renderFormEnd?: () => React.ReactElement;
    customValidators?: CustomValidators;
    nonDirtyActions?: Action[];
    extraFieldNames?: string[];
    onCancel?: () => void;
    saveIcon?: IconName;
};

const nameOrCompanyRequired = (
    value: any,
    allValues: Partial<ContactDetails>,
    allTouched: Partial<Record<ContactDetailField, boolean>>,
) => {
    const nameMissing = !(allValues.firstName && allValues.lastName) && allTouched.firstName && allTouched.lastName;
    const companyMissing = !allValues.company;
    if (nameMissing && companyMissing) {
        return intl('nameOrCompanyRequired');
    }
};
export const nameOrCompanyRequiredCustomValidators: CustomValidators = {
    firstName: nameOrCompanyRequired,
    lastName: nameOrCompanyRequired,
    company: nameOrCompanyRequired,
};

export type ContactDetailsFormProps = ContactDetailsFormOwnProps;

export type UseGetRenderContactDetailsFormFieldsArgs = Pick<
    ContactDetailsFormProps,
    'isDisabled' | 'required' | 'fields' | 'fieldNamePrefix' | 'customValidators' | 'extraFieldNames'
>;

export type HandleUpdateAfterSubmit = (
    initialValues: Partial<ContactDetails> | undefined,
    formValues: Partial<ContactDetails>,
) => Partial<ContactDetails>;

export type UseGetRenderContactDetailsFormFieldsReturnValue = {
    renderFields: () => React.ReactElement[];
    handleUpdateAfterSubmit: HandleUpdateAfterSubmit;
};

const maybePrefixName = (name: string, fieldNamePrefix: string | undefined) =>
    fieldNamePrefix ? `${fieldNamePrefix}.${name}` : name;

const ContactDetailsField: React.FC<{
    field: ContactDetailField;
    required?: ContactDetailField[];
    customValidators?: CustomValidators;
    fieldNamePrefix?: string;
    isDisabled?: boolean;
    fieldProps?: any;
    component:
        | typeof FormikInputs.Input
        | typeof FormikInputs.Select
        | typeof FormikInputs.TextArea
        | typeof FormikInputs.DateTimePicker
        | typeof FormikInputs.PhoneNumber;
    defaultValidators?: Validator[];
}> = ({ field, required, customValidators, fieldNamePrefix, isDisabled, fieldProps, defaultValidators, component }) => {
    const getIsRequired = (field: ContactDetailField) => (required || []).includes(field);
    const { values, touched } = useFormikContext();
    const contactDetailsValues = useMemo(() => {
        const contactDetailsValues: Partial<ContactDetails> = {};
        for (const field of allContactDetailFields) {
            contactDetailsValues[field] = getIn(values, maybePrefixName(field, fieldNamePrefix));
        }
        return contactDetailsValues;
    }, [fieldNamePrefix, values]);
    const contactDetailsTouched = useMemo(() => {
        const contactDetailsTouched: Partial<Record<ContactDetailField, boolean>> = {};
        for (const field of allContactDetailFields) {
            contactDetailsTouched[field] = getIn(touched, maybePrefixName(field, fieldNamePrefix));
        }
        return contactDetailsTouched;
    }, [fieldNamePrefix, touched]);

    const getCustomValidator = () => {
        if (!customValidators) return;

        const customValidator = customValidators[field];
        if (!customValidator) return;

        return (value: any) => customValidator(value, contactDetailsValues, contactDetailsTouched);
    };

    const getValidator = (field: ContactDetailField) => {
        const isRequired = getIsRequired(field);

        const allValidators = [
            ...(isRequired ? [requiredValidator] : []),
            ...(defaultValidators || []),
            getCustomValidator(),
        ].filter(isPresent);

        return composeValidators(...allValidators);
    };

    const getCommonProps = (field: ContactDetailField) => ({
        isRequired: getIsRequired(field),
        title: intl(field),
        isDisabled: isDisabled || false,
        name: maybePrefixName(field, fieldNamePrefix),
    });

    // if a custom validator relies on other fields, we need to use Field instead of FastField
    const FieldComponent = getCustomValidator() ? Field : FastField;

    return (
        <FieldComponent
            key={field}
            {...getCommonProps(field)}
            component={component}
            validate={getValidator(field)}
            {...(fieldProps || {})}
        />
    );
};

export const useGetRenderContactDetailsFormFields = ({
    isDisabled,
    required,
    fields,
    fieldNamePrefix,
    customValidators,
    extraFieldNames,
}: UseGetRenderContactDetailsFormFieldsArgs): UseGetRenderContactDetailsFormFieldsReturnValue => {
    const sharedProps = {
        required,
        customValidators,
        fieldNamePrefix,
        isDisabled,
    };

    const fieldRenderers: Record<ContactDetailField, () => React.ReactElement> = {
        firstName: () => (
            <ContactDetailsField key="firstName" field="firstName" component={FormikInputs.Input} {...sharedProps} />
        ),
        lastName: () => (
            <ContactDetailsField key="lastName" field="lastName" component={FormikInputs.Input} {...sharedProps} />
        ),
        company: () => (
            <ContactDetailsField key="company" field="company" component={FormikInputs.Input} {...sharedProps} />
        ),
        preferredFirstName: () => (
            <ContactDetailsField
                key="preferredFirstName"
                field="preferredFirstName"
                component={FormikInputs.Input}
                {...sharedProps}
            />
        ),
        dob: () => (
            <ContactDetailsField
                key="dob"
                field="dob"
                component={FormikInputs.DateTimePicker}
                {...sharedProps}
                defaultValidators={[getDateFormatValidator('MM/DD/YYYY')]}
            />
        ),
        sex: () => (
            <ContactDetailsField
                key="sex"
                field="sex"
                component={FormikInputs.Select}
                fieldProps={{ selectOptions: sexOptions, allowEmpty: true, placeholder: intl('selectSex') }}
                {...sharedProps}
            />
        ),
        gender: () => (
            <ContactDetailsField
                key="gender"
                field="gender"
                component={FormikInputs.Select}
                fieldProps={{
                    selectOptions: genderOptions,
                    allowEmpty: true,
                    placeholder: intl('selectGender'),
                }}
                {...sharedProps}
            />
        ),
        address1: () => (
            <ContactDetailsField key="address1" field="address1" component={FormikInputs.Input} {...sharedProps} />
        ),
        address2: () => (
            <ContactDetailsField key="address2" field="address2" component={FormikInputs.Input} {...sharedProps} />
        ),
        city: () => <ContactDetailsField key="cty" field="city" component={FormikInputs.Input} {...sharedProps} />,
        state: () => <ContactDetailsField key="state" field="state" component={FormikInputs.Input} {...sharedProps} />,
        zipCode: () => (
            <ContactDetailsField key="zipCode" field="zipCode" component={FormikInputs.Input} {...sharedProps} />
        ),
        email: () => (
            <ContactDetailsField
                key="email"
                field="email"
                component={FormikInputs.Input}
                {...sharedProps}
                defaultValidators={[email]}
            />
        ),
        phoneNumber: () => (
            <ContactDetailsField
                key="phoneNumber"
                field="phoneNumber"
                component={FormikInputs.PhoneNumber}
                {...sharedProps}
                defaultValidators={[phoneNumber]}
            />
        ),
        relationship: () => (
            <ContactDetailsField
                key="relationship"
                field="relationship"
                component={FormikInputs.Input}
                {...sharedProps}
            />
        ),
        description: () => (
            <ContactDetailsField
                key="description"
                field="description"
                component={FormikInputs.TextArea}
                {...sharedProps}
            />
        ),
        faxNumber: () => (
            <ContactDetailsField
                key="faxNumber"
                field="faxNumber"
                component={FormikInputs.PhoneNumber}
                {...sharedProps}
                defaultValidators={[faxNumber]}
            />
        ),
        url: () => <ContactDetailsField key="url" field="url" component={FormikInputs.Input} {...sharedProps} />,
        role: () => <ContactDetailsField key="role" field="role" component={FormikInputs.Input} {...sharedProps} />,
    };

    return {
        renderFields: () => {
            const renderedFields = fields.map((field) => fieldRenderers[field]());
            const chunked = _chunk(renderedFields, 2);
            return chunked.map((chunk, index) => <SplitFormRow key={index}>{chunk}</SplitFormRow>);
        },
        handleUpdateAfterSubmit: (initialValues, formValues) => {
            const copiedOriginal = { ...initialValues };
            for (const field of Object.keys(fieldRenderers) as ContactDetailField[]) {
                copiedOriginal[field] = formValues[field] as any;
            }

            if (extraFieldNames) {
                for (const field of extraFieldNames) {
                    (copiedOriginal as any)[field] = (formValues as any)[field];
                }
            }

            return copiedOriginal as ContactDetails;
        },
    };
};

const ContactDetailsForm: React.FC<ContactDetailsFormProps> = (props) => {
    const { handleUpdateAfterSubmit, renderFields } = useGetRenderContactDetailsFormFields(props);

    return (
        <Form
            title={props.title}
            actionContainerSubmit
            isDisabled={props.isDisabled}
            nonDirtyActions={props.nonDirtyActions}
            saveText={props.submitButtonTitle}
            initialValues={props.initialValues || {}}
            onSubmit={(values, helpers) => {
                props.onSubmit(handleUpdateAfterSubmit(props.initialValues, values) as ContactDetails);
                handleFormikSubmit(values, helpers);
            }}
            onCancel={props.onCancel}
            cancelButtonVariant="secondary"
            saveIcon={props.saveIcon}
        >
            {() => {
                return (
                    <FormStack>
                        {props.renderFormStart?.() || null}
                        {renderFields()}
                        {props.renderFormEnd?.() || null}
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(ContactDetailsForm);
